import {
  ComponentIcon,
  getTaxonomyColor,
  SystemIcon,
} from '@agilelab/plugin-wb-platform';
import { useMemo } from 'react';
import { Box, makeStyles, Theme } from '@material-ui/core';
import { get } from 'lodash';
import { entityRefToUrn } from '@agilelab/plugin-wb-builder-common';

const useStyles = makeStyles<Theme, { taxonomyId?: string }>((theme: Theme) => {
  return {
    avatarBase: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: theme.palette.primary.main,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'contain',
      fontSize: '1.125rem',
      textTransform: 'uppercase',
      pointerEvents: 'none',
      borderRadius: 8,
      flexShrink: 0,
    },

    small: {
      width: 24,
      height: 24,
      padding: 2,
    },
    large: {
      width: '100%',
      height: '100%',
    },
    defaultIcon: (props: { taxonomyId?: string }) => {
      const bgColor = getTaxonomyColor(props.taxonomyId ?? '', theme);
      return {
        minWidth: '24px',
        width: '100%',
        height: '100%',
        color: bgColor,
        flexShrink: 0,
      };
    },
  };
});

const getInitials = (title: string) =>
  title
    .split(' ')
    .slice(0, 2)
    .map(s => s.charAt(0).toUpperCase())
    .join('');

export function useTemplateIcon(
  entity: any,
  variant: 'small' | 'large',
  iconPath?: string,
) {
  const taxonomyRef = entity?.metadata?.classDetails?.taxonomy;

  const taxonomyUrn = useMemo(() => {
    try {
      return entityRefToUrn(taxonomyRef);
    } catch (_e) {
      return undefined;
    }
  }, [taxonomyRef]);

  const classes = useStyles({
    taxonomyId: taxonomyUrn,
  });

  const icon = iconPath ? get(entity, iconPath) : entity?.metadata?.mesh?.icon;
  const title = entity?.metadata?.title;

  const renderedIcon = useMemo(() => {
    if (!icon) {
      if (entity?.metadata?.classDetails?.type === 'systemtype') {
        return <SystemIcon className={classes.defaultIcon} />;
      }

      if (entity?.metadata?.classDetails?.type === 'componenttype') {
        return <ComponentIcon className={classes.defaultIcon} />;
      }
    }

    if (icon) {
      return (
        <img
          src={icon}
          alt={title && variant === 'large' ? title : 'icon'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      );
    }

    if (title) {
      return getInitials(title);
    }

    return null;
  }, [
    classes.defaultIcon,
    entity?.metadata?.classDetails?.type,
    icon,
    title,
    variant,
  ]);

  return (
    <Box className={`${classes.avatarBase} ${classes[variant]}`}>
      {renderedIcon}
    </Box>
  );
}
